import { ImagePlus, Trash2 } from 'lucide-react';
import { useId, useRef, useState, type ReactNode } from 'react';
import { errorMessage } from '../lib/api';
import { formatBytes } from './formatBytes';
import { checkImage, IMAGE_ACCEPT, useObjectUrl } from './imageFiles';
import './upload.css';

interface Props {
  label: string;
  /** Aktuelles Bild (null = keins). */
  url: string | null;
  /** Hochladen; Fehler werden angezeigt. */
  onUpload: (file: File) => Promise<unknown>;
  onRemove?: () => Promise<unknown>;
  maxBytes: number;
  shape?: 'circle' | 'banner' | 'square';
  hint?: ReactNode;
  /** Platzhalter ohne Bild (z. B. Initialen). */
  placeholder?: ReactNode;
  disabled?: boolean;
  testId: string;
}

/** Bild hochladen mit Vorschau, Fortschrittsanzeige und Entfernen (Server-Icon, Banner, Rollen-Icon …). */
export function ImageUpload({ label, url, onUpload, onRemove, maxBytes, shape = 'circle', hint, placeholder, disabled, testId }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = useObjectUrl(pending);
  const shown = preview ?? url;
  const errId = useId();

  const pick = async (file: File | undefined) => {
    if (!file) return;
    const err = checkImage(file, maxBytes);
    setError(err);
    if (err) return;
    setPending(file);
    setBusy(true);
    try {
      await onUpload(file);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPending(null);
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!onRemove) return;
    setBusy(true);
    setError(null);
    try {
      await onRemove();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="image-upload" data-testid={testId}>
      <span className="label">{label}</span>
      <div className="image-upload-row">
        <button
          type="button"
          className={`image-upload-preview shape-${shape}`}
          onClick={() => input.current?.click()}
          disabled={disabled || busy}
          aria-label={`${label} auswählen`}
          aria-describedby={error ? errId : undefined}
          data-testid={`${testId}-preview`}
        >
          {shown ? <img src={shown} alt="" draggable={false} /> : (placeholder ?? <ImagePlus size={28} aria-hidden="true" />)}
          {busy && (
            <span className="image-upload-busy">
              <span className="spinner" aria-label="Wird hochgeladen" />
            </span>
          )}
        </button>
        <div className="stack" style={{ gap: '0.4rem' }}>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-sm" onClick={() => input.current?.click()} disabled={disabled || busy} data-testid={`${testId}-pick`}>
              <ImagePlus size={16} aria-hidden="true" /> {url ? 'Ändern' : 'Hochladen'}
            </button>
            {url && onRemove && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => void remove()} disabled={disabled || busy} data-testid={`${testId}-remove`}>
                <Trash2 size={16} aria-hidden="true" /> Entfernen
              </button>
            )}
          </div>
          <span className="field-hint">{hint ?? `PNG, JPEG, GIF oder WebP (animiert erlaubt), höchstens ${formatBytes(maxBytes)}.`}</span>
          {error && (
            <span className="error-text" role="alert" id={errId} data-testid={`${testId}-error`}>
              {error}
            </span>
          )}
        </div>
      </div>
      <input
        ref={input}
        type="file"
        accept={IMAGE_ACCEPT}
        hidden
        onChange={(e) => {
          void pick(e.target.files?.[0]);
          e.target.value = '';
        }}
        data-testid={`${testId}-input`}
      />
    </div>
  );
}
